import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, RefreshCw, Scale, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { api, errMsg, fmtDateTime, downloadBlob } from "../../lib/api";

// accounting precision: always two decimals (fmtINR drops trailing zeros)
const money = (n) => "\u20B9" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Phase 212 — Import Reconciliation Report.
 * Compares an import batch (Third Party Bulk Entry or Bulk Excel Upload) with what is in the database RIGHT NOW:
 * 6 summary figures, reason breakdown, and the list of failed / mismatched / drifted rows. Export to Excel.
 * Props: code, source ("third_party" | "excel"), batchId, filename (optional), open, onOpenChange.
 */
const KIND_CLS = {
  import: "bg-rose-50 text-rose-800 border-rose-200",
  drift: "bg-amber-50 text-amber-900 border-amber-200",
  info: "bg-slate-100 text-slate-700 border-slate-200",
};
const STATUS_CLS = {
  created: "bg-emerald-50 text-emerald-700 border-emerald-200",
  duplicate: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
};

const Amount = ({ value, testId, muted = false }) => value == null
  ? <span className="text-xs italic text-muted-foreground" data-testid={testId}>not recorded</span>
  : <span className={`tabular-nums ${muted ? "text-muted-foreground" : ""}`} data-testid={testId}>{money(value)}</span>;

export function ImportReconciliationDialog({ code, source, batchId, filename, open, onOpenChange, testIdPrefix = "recon" }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!batchId || !source) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/t/${code}/upload-reports/reconciliation/${source}/${batchId}`);
      setReport(r.data);
    } catch (e) { setError(errMsg(e)); setReport(null); } finally { setLoading(false); }
  }, [code, source, batchId]);

  useEffect(() => { if (open) { setCat("all"); setQ(""); load(); } }, [open, load]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      await downloadBlob(`/t/${code}/upload-reports/reconciliation/${source}/${batchId}/export`,
        `reconciliation_${(filename || report?.batch?.filename || "import").replace(/\.[^.]+$/, "")}.xlsx`);
      toast.success("Reconciliation report exported", { description: "Summary and Mismatches sheets included." });
    } catch (e) { toast.error(errMsg(e)); } finally { setExporting(false); }
  };

  const s = report?.summary;
  const rows = useMemo(() => {
    const all = report?.mismatches || [];
    const needle = q.trim().toLowerCase();
    return all.filter((m) => (cat === "all" || (m.categories || [m.category]).includes(cat))
      && (!needle || `${m.pin || ""} ${m.owner_name || ""} ${m.reason || ""}`.toLowerCase().includes(needle)));
  }, [report, cat, q]);

  const diffTone = !s ? "" : Math.abs(s.difference) < 0.005 ? "text-emerald-700" : s.difference > 0 ? "text-rose-700" : "text-amber-700";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2 flex-wrap">
            <Scale className="h-4 w-4" /> Import Reconciliation Report
            {report?.batch && (
              <Badge variant="outline" className="font-normal text-[10px]" data-testid={`${testIdPrefix}-source-badge`}>{report.batch.source_label}</Badge>
            )}
            {s && (s.balanced
              ? <Badge variant="outline" className="font-normal text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200 gap-1" data-testid={`${testIdPrefix}-balanced-badge`}><CheckCircle2 className="h-3 w-3" /> Balanced</Badge>
              : <Badge variant="outline" className="font-normal text-[10px] bg-rose-50 text-rose-700 border-rose-200 gap-1" data-testid={`${testIdPrefix}-differences-badge`}><AlertTriangle className="h-3 w-3" /> {s.mismatch_count} record{s.mismatch_count === 1 ? "" : "s"} to review</Badge>)}
          </DialogTitle>
          <DialogDescription className="text-xs" data-testid={`${testIdPrefix}-description`}>
            {report?.batch ? (
              <>
                <span className="font-medium text-foreground">{report.batch.filename || "import"}</span>
                {report.batch.fy_label ? ` · ${report.batch.fy_label}` : ""} · imported {fmtDateTime(report.batch.created_at)}{report.batch.by ? ` by ${report.batch.by}` : ""}
                {" · "}compared with the live database {fmtDateTime(report.generated_at)}
              </>
            ) : "Compares the imported file with what was actually saved in the database — rebuilt live every time it opens."}
          </DialogDescription>
        </DialogHeader>

        {loading && !report ? (
          <div className="space-y-3" data-testid={`${testIdPrefix}-loading`}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
            <Skeleton className="h-40 w-full" />
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm space-y-2" data-testid={`${testIdPrefix}-error`}>
            <p className="font-medium text-destructive">Could not build the report</p>
            <p className="text-muted-foreground">{error}</p>
            <Button size="sm" variant="outline" onClick={load} data-testid={`${testIdPrefix}-retry`}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Try again</Button>
          </div>
        ) : report && s ? (
          <div className="space-y-4">
            {/* ---- 6 summary cards ---- */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={`${testIdPrefix}-summary`}>
              <SummaryCard label="Records in file" value={s.file_records} testId={`${testIdPrefix}-file-records`}
                hint={s.data_records !== s.file_records ? `${s.data_records} with data` : null} />
              <SummaryCard label="Saved in database" value={s.saved_records} tone="text-emerald-700" testId={`${testIdPrefix}-saved-records`}
                hint={s.drift_records ? `${s.drift_records} changed since` : null} />
              <SummaryCard label="Failed / skipped" value={s.failed_records + s.skipped_records} tone={(s.failed_records + s.skipped_records) ? "text-rose-700" : ""} testId={`${testIdPrefix}-failed-records`}
                hint={`${s.failed_records} failed · ${s.skipped_records} skipped`} />
              <SummaryCard label="Amount in file" value={money(s.file_amount)} testId={`${testIdPrefix}-file-amount`}
                hint={s.file_amount_unknown_rows ? `${s.file_amount_unknown_rows} row${s.file_amount_unknown_rows === 1 ? "" : "s"} not recorded` : null} />
              <SummaryCard label="Amount in database" value={money(s.db_amount)} tone="text-emerald-700" testId={`${testIdPrefix}-db-amount`} />
              <SummaryCard label="Difference" value={`${s.difference > 0 ? "" : s.difference < 0 ? "−" : ""}${money(Math.abs(s.difference))}`} tone={diffTone} testId={`${testIdPrefix}-difference`}
                hint={Math.abs(s.difference) < 0.005 ? "file = database" : s.difference > 0 ? "file > database" : "database > file"} />
            </div>

            {/* ---- reason breakdown + notes ---- */}
            {report.categories?.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-categories`}>
                {report.categories.map((c) => (
                  <button key={c.key} type="button" onClick={() => setCat(cat === c.key ? "all" : c.key)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${KIND_CLS[c.kind]} ${cat === c.key ? "ring-2 ring-primary/40" : "hover:opacity-80"}`}
                    data-testid={`${testIdPrefix}-cat-${c.key}`} aria-pressed={cat === c.key}>
                    {c.label} <b className="tabular-nums">{c.count}</b>
                  </button>
                ))}
              </div>
            )}
            {report.notes?.length > 0 && (
              <ul className="text-[11px] text-muted-foreground list-disc pl-4 space-y-0.5" data-testid={`${testIdPrefix}-notes`}>
                {report.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}

            {/* ---- toolbar ---- */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <p className="text-sm font-medium flex-1" data-testid={`${testIdPrefix}-table-title`}>
                Mismatched / failed records <span className="text-muted-foreground font-normal">({rows.length}{rows.length !== (report.mismatches || []).length ? ` of ${report.mismatches.length}` : ""})</span>
              </p>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PIN / owner / reason" className="h-8 pl-7 w-full sm:w-56 text-xs" data-testid={`${testIdPrefix}-search`} />
              </div>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="h-8 w-full sm:w-56 text-xs" data-testid={`${testIdPrefix}-cat-filter`}><SelectValue placeholder="All reasons" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reasons</SelectItem>
                  {(report.categories || []).filter((c) => c.key !== "not_processed").map((c) => <SelectItem key={c.key} value={c.key}>{c.label} ({c.count})</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8" onClick={load} disabled={loading} data-testid={`${testIdPrefix}-refresh`}>
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button size="sm" className="h-8" onClick={exportXlsx} disabled={exporting} data-testid={`${testIdPrefix}-export`}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> {exporting ? "Preparing…" : "Export to Excel"}
              </Button>
            </div>

            {/* ---- mismatch table ---- */}
            {(report.mismatches || []).length === 0 ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-start gap-2" data-testid={`${testIdPrefix}-empty`}>
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Every record in the file is in the database with the same amount — nothing to reconcile.</span>
              </div>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center border rounded-md" data-testid={`${testIdPrefix}-no-match`}>No records match this filter.</p>
            ) : (
              <div className="border rounded-md overflow-x-auto max-h-[46vh] overflow-y-auto">
                <Table data-testid={`${testIdPrefix}-table`}>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="h-8 text-xs w-12">Row</TableHead>
                      <TableHead className="h-8 text-xs">PIN</TableHead>
                      <TableHead className="h-8 text-xs">Owner Name</TableHead>
                      <TableHead className="h-8 text-xs text-right">Amount in file</TableHead>
                      <TableHead className="h-8 text-xs text-right">Amount in database</TableHead>
                      <TableHead className="h-8 text-xs">Reason for mismatch</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((m, i) => (
                      <TableRow key={`${m.row_number}-${i}`} data-testid={`${testIdPrefix}-row-${i}`} data-category={m.category}>
                        <TableCell className="py-1.5 text-xs tabular-nums text-muted-foreground">{m.row_number ?? "—"}</TableCell>
                        <TableCell className="py-1.5 text-xs font-mono" data-testid={`${testIdPrefix}-row-${i}-pin`}>{m.pin || <span className="text-muted-foreground italic font-sans">auto / blank</span>}</TableCell>
                        <TableCell className="py-1.5 text-xs" data-testid={`${testIdPrefix}-row-${i}-owner`}>
                          {m.owner_name || <span className="text-muted-foreground">—</span>}
                          {m.status && <Badge variant="outline" className={`ml-1.5 text-[9px] font-normal capitalize ${STATUS_CLS[m.status] || ""}`}>{m.status}</Badge>}
                        </TableCell>
                        <TableCell className="py-1.5 text-xs text-right"><Amount value={m.file_amount} testId={`${testIdPrefix}-row-${i}-file-amount`} /></TableCell>
                        <TableCell className="py-1.5 text-xs text-right">
                          {m.db_amount == null ? <span className="text-muted-foreground">—</span> : <Amount value={m.db_amount} testId={`${testIdPrefix}-row-${i}-db-amount`} />}
                          {m.difference != null && Math.abs(m.difference) >= 0.005 && m.status !== "failed" && m.status !== "duplicate" && (
                            <p className={`text-[10px] tabular-nums ${m.difference > 0 ? "text-rose-700" : "text-amber-700"}`}>{m.difference > 0 ? "−" : "+"}{money(Math.abs(m.difference))}</p>
                          )}
                        </TableCell>
                        <TableCell className="py-1.5 text-xs max-w-[380px]">
                          <Badge variant="outline" className={`text-[10px] font-normal mr-1.5 ${KIND_CLS[m.kind] || KIND_CLS.info}`} data-testid={`${testIdPrefix}-row-${i}-category`}>{m.category_label}</Badge>
                          <span className="text-muted-foreground" data-testid={`${testIdPrefix}-row-${i}-reason`}>{m.reason}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        ) : null}
        <div className="flex justify-end pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid={`${testIdPrefix}-close`}><X className="h-3.5 w-3.5 mr-1.5" /> Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ label, value, hint, tone = "", testId }) {
  return (
    <div className="rounded-lg border p-2.5 min-w-0" data-testid={testId}>
      <p className="text-[11px] text-muted-foreground truncate" title={label}>{label}</p>
      <p className={`text-lg font-semibold tabular-nums leading-tight truncate ${tone}`} title={String(value)} data-testid={`${testId}-value`}>{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground truncate" title={hint} data-testid={`${testId}-hint`}>{hint}</p>}
    </div>
  );
}

/** Small trigger button used in result panels / batch history rows. */
export function ReconcileButton({ onClick, size = "sm", variant = "outline", className = "", label = "Reconcile", testId = "recon-open" }) {
  return (
    <Button type="button" variant={variant} size={size} onClick={onClick} className={className} data-testid={testId} title="Import Reconciliation Report — compare the file with the database">
      <Scale className={`${size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} mr-1.5`} /> {label}
    </Button>
  );
}
